import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { buildYears, buildDays, MONTHS } from '~/utils/dueDate';

import css from './DueDateFilter.less';

const DownIcon = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30" fill="none">
      <g opacity="0.75">
        <circle cx="15" cy="15" r="15" fill="#EFEDEC" />
        <path d="M9 12.25L15 18.25L21 12.25" stroke="#918D89" strokeWidth="2" strokeLinejoin="round" />
      </g>
    </svg>
  );
};

const iconCalendar = require('~/static/index/icon_calendar.png');

const DueDateFilter = ({ onSubmit }) => {
  const years = useMemo(() => buildYears(), []);
  const [year, setYear] = useState(years[0]);
  const [month, setMonth] = useState(MONTHS[new Date().getMonth()]);
  const [day, setDay] = useState('');
  const yearSelectRef = useRef(null);
  const monthSelectRef = useRef(null);
  const daySelectRef = useRef(null);

  const [uniqueId, setUniqueId] = useState('default');

  useEffect(() => {
    setUniqueId(
      Math.random()
        .toString(36)
        .slice(2, 8),
    );
  }, []);

  const controlIds = useMemo(() => {
    return {
      year: `due-date-year-${uniqueId}`,
      month: `due-date-month-${uniqueId}`,
      day: `due-date-day-${uniqueId}`,
    };
  }, [uniqueId]);

  const daysInMonth = useMemo(() => {
    if (!month) {
      return buildDays(31);
    }
    const date = new Date(year, month, 0);
    return buildDays(date.getDate());
  }, [year, month]);

  const handleSubmit = event => {
    event.preventDefault();
    if (onSubmit && year && month && day) {
      onSubmit({ year, month, day });
    }
  };

  const handleArrowClick = targetRef => {
    const select = targetRef?.current;
    if (!select) {
      return;
    }
    if (typeof select.showPicker === 'function') {
      select.showPicker();
      return;
    }
    select.focus();
    let mouseDownEvent;
    if (typeof window !== 'undefined' && typeof window.MouseEvent === 'function') {
      mouseDownEvent = new window.MouseEvent('mousedown', {
        bubbles: true,
        cancelable: true,
      });
    } else if (typeof document !== 'undefined') {
      mouseDownEvent = document.createEvent('MouseEvents');
      mouseDownEvent.initEvent('mousedown', true, true);
    }
    if (mouseDownEvent) {
      select.dispatchEvent(mouseDownEvent);
    }
    select.click();
  };

  return (
    <section className={css.wrapper}>
      <form className={css.filterBar} onSubmit={handleSubmit}>
        <div className={css.leading}>
          <img src={iconCalendar} alt="" aria-hidden="true" />
          <span>
            选择预产期，<span className={css.english}>Get</span> 心仪月嫂
          </span>
        </div>
        <div className={css.actionRow}>
          <div className={css.controls}>
            <label className={css.selectWrap} htmlFor={controlIds.year}>
              <span className={css.visuallyHidden}>预产年份</span>
              <div className={css.selectValue}>
                <select
                  className={css.selectControl}
                  id={controlIds.year}
                  ref={yearSelectRef}
                  value={year}
                  onChange={event => setYear(Number(event.target.value))}
                >
                  {years.map(option => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <span className={css.selectSuffix}>年</span>
              </div>
              <button
                type="button"
                className={css.selectArrow}
                onClick={() => handleArrowClick(yearSelectRef)}
                aria-label="展开年份选项"
              >
                <DownIcon />
              </button>
            </label>
            <label className={classNames(css.selectWrap)} htmlFor={controlIds.month}>
              <span className={css.visuallyHidden}>预产月份</span>
              <div className={css.selectValue}>
                <select
                  className={css.selectControl}
                  id={controlIds.month}
                  ref={monthSelectRef}
                  value={month}
                  onChange={event => setMonth(Number(event.target.value))}
                >
                  {MONTHS.map(option => (
                    <option key={option} value={option}>
                      {String(option).padStart(2, '0')}
                    </option>
                  ))}
                </select>
                <span className={css.selectSuffix}>月</span>
              </div>
              <button
                type="button"
                className={css.selectArrow}
                onClick={() => handleArrowClick(monthSelectRef)}
                aria-label="展开月份选项"
              >
                <DownIcon />
              </button>
            </label>
            <label className={classNames(css.selectWrap)} htmlFor={controlIds.day}>
              <span className={css.visuallyHidden}>预产日期</span>
              <div className={css.selectValue}>
                <select
                  className={classNames(css.selectControl, {
                    [css.selectPlaceholder]: !day,
                  })}
                  id={controlIds.day}
                  ref={daySelectRef}
                  value={day}
                  onChange={event => setDay(event.target.value)}
                >
                  <option value="">--</option>
                  {daysInMonth.map(option => (
                    <option key={option} value={option}>
                      {String(option).padStart(2, '0')}
                    </option>
                  ))}
                </select>
                <span
                  className={classNames(css.selectSuffix, {
                    [css.selectSuffixPlaceholder]: !day,
                  })}
                >
                  日
                </span>
              </div>
              <button
                type="button"
                className={css.selectArrow}
                onClick={() => handleArrowClick(daySelectRef)}
                aria-label="展开日期选项"
              >
                <DownIcon />
              </button>
            </label>
          </div>
          <button className={css.submit} type="submit">
            筛选
          </button>
        </div>
      </form>
    </section>
  );
};

export default DueDateFilter;
